// src/components/tarifs/RecapList.jsx
import * as React from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Stack,
  Divider,
} from '@mui/material';

import { useI18n } from '../../i18n/I18nProvider';
import recapI18n from '../../i18n/components/tarifs/recap.i18n';
import pricelistI18n from '../../i18n/components/tarifs/pricelist.i18n';

const money = (n) => Number(n || 0).toFixed(2);

const stripKnownPrefix = (name) =>
  String(name || '').replace(/^\s*(ekran|écran|ecran|screen)\s+/i, '');

const strip = (s = '') => String(s).replace(/\s+/g, ' ').trim();

const norm = (s = '') => {
  try {
    return String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  } catch {
    return String(s).toLowerCase();
  }
};

const removeLeadingBrand = (left = '', brand = '') => {
  const l = strip(left);
  const b = strip(brand);
  if (!l || !b) return l;

  const ln = norm(l);
  const bn = norm(b);

  if (ln.startsWith(bn + ' ')) return l.slice(b.length).trim();
  if (ln.startsWith(bn + '-')) return l.slice(b.length + 1).trim();
  return l;
};

// ✅ Composants : on réutilise EXACTEMENT les mêmes libellés que la PriceList (tabs)
function normalizeComponentPrefix(raw = '', t) {
  let base = '';
  try {
    base = String(raw)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  } catch {
    base = String(raw).toLowerCase().trim();
  }

  // écran
  if (
    !base ||
    base.includes('ecran') ||
    base.includes('screen') ||
    base.includes('lcd') ||
    base.includes('display')
  ) {
    return t('tarifs.pricelist.prefix.screen', 'Écran');
  }

  // carte mère
  if (
    base.includes('carte mere') ||
    base.includes('motherboard') ||
    base.includes('mainboard') ||
    base.includes('logic board')
  ) {
    return t('tarifs.pricelist.parts.tabs.motherboard', 'Carte mère');
  }

  // connecteur charge
  if (
    (base.includes('connecteur') && base.includes('charge')) ||
    base.includes('charging port') ||
    base.includes('charging connector') ||
    base.includes('dock connector')
  ) {
    return t('tarifs.pricelist.parts.tabs.charging', 'Connecteur charge');
  }

  // caméra
  if (
    base.includes('camera') ||
    base.includes('caméra') ||
    base.includes('camera module') ||
    base.includes('module camera')
  ) {
    return t('tarifs.pricelist.parts.tabs.camera', 'Caméra');
  }

  // fallback
  return t('tarifs.recap.kind.part', 'Pièce détachée');
}

function formatRecapLabel(line, t, cleanSuffix) {
  const id = String(line?.id || '');
  const raw = String(line?.label || '');

  // On split sur — ou -/–/— (sûr pour tes labels)
  const parts = raw
    .split(/\s*[—–-]\s*/g)
    .map(strip)
    .filter(Boolean);

  // ✅ model:* = "<Préfixe écran> <Modèle>"
  if (id.startsWith('model:')) {
    // raw: "Apple iPhone 12 (128 Go) — Appareil complet"
    const left = parts[0] || raw;
    const cleanedLeft = cleanSuffix(stripKnownPrefix(left));
    const prefix = t('tarifs.pricelist.prefix.screen', 'Écran');
    return `${prefix} ${cleanedLeft}`.trim();
  }

  // ✅ component:* = "<Préfixe composant> <Modèle>" (même forme que les écrans)
  if (id.startsWith('component:')) {
    // raw: "Apple iPhone 12 — Carte mère"
    const leftBrandModel = parts[0] || raw;
    const rightComponent = parts.length >= 2 ? parts.slice(1).join(' — ') : '';

    const prefix = normalizeComponentPrefix(rightComponent, t);

    // Retire la marque si on l'a (sur tes lines: brand est un code "apple" => pas fiable)
    // Donc: on tente removeLeadingBrand seulement si brand ressemble à une marque humaine.
    // Si brand vaut "apple/samsung/oppo/autre" => on skip le removeLeadingBrand.
    const brand = String(line?.brand || '').trim();
    const canStripBrand = brand && !['apple', 'samsung', 'oppo', 'autre'].includes(brand.toLowerCase());

    const leftNoBrand = canStripBrand ? removeLeadingBrand(leftBrandModel, brand) : leftBrandModel;

    const cleanedModel = cleanSuffix(stripKnownPrefix(leftNoBrand));
    return `${prefix} ${cleanedModel}`.trim();
  }

  // ✅ brand:* (lot) -> même titre que PriceList
  if (id.startsWith('brand:')) {
    const low = raw.toLowerCase();
    if (low.includes('vendez en 1 clic') || low.includes('wyślij, a my posortujemy')) {
      return t('tarifs.pricelist.batchSort.title');
    }
    const cleaned = cleanSuffix(raw);
    return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
  }

  return cleanSuffix(raw);
}

export default function RecapList({
  summary,
  highlightedId,
  onLineClick,
  maxHeight = 260,
}) {
  const { t, register } = useI18n();

  React.useEffect(() => {
    register('tarifs', recapI18n);
    register('tarifs', pricelistI18n); // ✅ nécessaire pour tarifs.pricelist.*
  }, [register]);

  const lines = (summary?.lines || [])
    .map((l) => ({
      ...l,
      id: String(l.id),
      qty: Number(l.qty) || 0,
      price: Number(l.price) || 0,
    }))
    .filter((l) => l.qty > 0)
    .reverse();

  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = 0;
  }, [lines.length]);

  if (lines.length === 0) return null;

  const cleanSuffix = (text) =>
    String(text || '').replace(/\s+[-–—]\s*appareil complet$/i, '');

  const buildLabel = (line) => formatRecapLabel(line, t, cleanSuffix);

  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ mb: 1 }} />

      <Box
        ref={scrollRef}
        sx={{
          maxHeight,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.25)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.35)',
          },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.QTpaper',
            pb: 0.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 0.5 }}>
            <Typography
              sx={{
                flexGrow: 1,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'text.secondary',
              }}
            >
              {t('tarifs.recap.table.article')}
            </Typography>

            <Typography
              sx={{
                width: 40,
                fontSize: 11,
                fontWeight: 600,
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'text.secondary',
              }}
            >
              {t('tarifs.recap.table.qty')}
            </Typography>

            <Typography
              sx={{
                width: 82,
                fontSize: 11,
                fontWeight: 600,
                textAlign: 'right',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'text.secondary',
              }}
            >
              {t('tarifs.recap.table.total')}
            </Typography>
          </Stack>
        </Box>

        <List dense sx={{ pt: 0, px: 0.5 }}>
          {lines.map((l, idx) => {
            const active = highlightedId === l.id;
            const displayLabel = buildLabel(l);
            const total = money(l.price * l.qty);

            return (
              <React.Fragment key={l.id}>
                <ListItemButton
                  onClick={() => onLineClick?.(l.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.25,
                    pl: 0,
                    pr: 0.5,
                    transition:
                      'background-color 0.25s ease, box-shadow 0.25s ease, transform 0.15s ease',
                    ...(active
                      ? {
                          bgcolor: 'rgba(4,102,200,0.12)',
                          boxShadow: '0 0 0 1px rgba(4,102,200,0.45)',
                          transform: 'scale(1.01)',
                        }
                      : {
                          '&:hover': { bgcolor: '#f6f7f6', transform: 'translateX(1px)' },
                        }),
                  }}
                >
                  <ListItemText
                    sx={{ pl: 0 }}
                    primary={
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
                        <Typography
                          sx={{
                            flexGrow: 1,
                            fontSize: 13.5,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'left',
                          }}
                        >
                          {displayLabel}
                        </Typography>

                        <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
                          <Chip
                            size="small"
                            label={`× ${l.qty}`}
                            sx={{ height: 20, fontSize: 11, minWidth: 40, justifyContent: 'center' }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            width: 82,
                            fontSize: 13,
                            fontWeight: 700,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            color: 'black',
                          }}
                        >
                          {total} €
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItemButton>

                {idx < lines.length - 1 && (
                  <Divider component="li" sx={{ my: 0.25, opacity: 0.4, ml: 0.5, mr: 0.5 }} />
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}